import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { CheckCircle2, Heart, Loader2, ArrowRight } from "lucide-react";

const MAX_POLLS = 12;     // ~24s
const POLL_INTERVAL = 2000;

export default function SponsorThankYou() {
  const [params] = useSearchParams();
  // Accept both new (order_id) and legacy (session_id) for backwards compat
  const orderId = params.get("order_id") || params.get("session_id");
  const [tx, setTx] = useState(null);
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const pollsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!orderId) { setStatus("error"); return; }

    const poll = async () => {
      try {
        const { data } = await api.get(`/sponsor/status/${orderId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "failed" || data.status === "expired") {
          setStatus("expired");
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setStatus("timeout");
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setStatus("error");
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [orderId]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-16">
        <div className="brand-card p-10 text-center" data-testid="sponsor-thankyou-card">
          {status === "polling" && (
            <>
              <Loader2 className="mx-auto text-primary animate-spin mb-4" size={36} />
              <h1 className="text-3xl font-extrabold text-[#3D405B] tracking-tight">Just confirming your gift…</h1>
              <p className="text-[#7A7D96] mt-2">This usually takes a few seconds. Hold tight.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-secondary/15 text-secondary flex items-center justify-center mb-4">
                <CheckCircle2 size={36} strokeWidth={2.4} />
              </div>
              <div className="overline mb-1">Thank you</div>
              <h1 className="text-4xl font-extrabold text-[#3D405B] tracking-tight">You just funded {tx?.sessions || 1} session{(tx?.sessions || 1) > 1 ? "s" : ""}.</h1>
              <p className="text-[#5F627D] mt-3 leading-relaxed">
                Your sponsorship goes directly to a young South African who needs a safe place to talk. From the whole Hello Foundation team — thank you.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-accent/30 text-[#3D405B] font-bold">
                <Heart size={16} className="text-primary" />
                {tx?.amount ? new Intl.NumberFormat("en-ZA", { style: "currency", currency: (tx.currency || "ZAR").toUpperCase(), maximumFractionDigits: 0 }).format(tx.amount) : ""}
              </div>
              <div className="mt-8 flex justify-center gap-3">
                <Link to="/" className="px-6 py-3 rounded-full border-2 border-[#3D405B]/15 font-bold text-[#3D405B] hover:bg-[#3D405B]/5 transition-all">Back to home</Link>
                <Link to="/#sponsor" className="inline-flex items-center gap-1.5 px-6 py-3 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all" data-testid="sponsor-thankyou-again-btn">
                  Sponsor again <ArrowRight size={15} />
                </Link>
              </div>
            </>
          )}
          {(status === "expired" || status === "timeout" || status === "error") && (
            <>
              <h1 className="text-2xl font-extrabold text-[#3D405B] tracking-tight">Couldn&apos;t confirm your payment</h1>
              <p className="text-[#7A7D96] mt-2">{status === "expired" ? "The checkout session expired." : "We didn't see confirmation in time. If you were charged, please email us — we'll sort it."}</p>
              <Link to="/" className="mt-6 inline-flex items-center gap-1.5 px-6 py-3 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all">
                Back to home <ArrowRight size={15} />
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
